import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { Inbox } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * An empty state that teaches the next step.
 *
 * "沒有資料" tells the operator something they can already see. Every empty
 * state here says what would put data on the screen, and where the button for
 * it is — so `body` is required, and `action` is expected wherever the operator
 * can actually do something about it.
 */
export function EmptyState({
  icon: Icon = Inbox,
  title,
  body,
  action,
  className,
  compact = false,
}: {
  icon?: LucideIcon;
  title: string;
  body: ReactNode;
  action?: ReactNode;
  className?: string;
  compact?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center rounded-lg border border-dashed border-[var(--color-border)] text-center",
        compact ? "gap-1.5 px-4 py-8" : "gap-2 px-6 py-14",
        className,
      )}
    >
      <Icon
        size={compact ? 18 : 22}
        className="text-[var(--color-fg-faint)]"
        aria-hidden
      />
      <div className="text-sm font-medium tracking-[-0.165px] text-[var(--color-fg)]">{title}</div>
      <p className="max-w-md text-xs leading-relaxed text-[var(--color-fg-subtle)]">{body}</p>
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}
